import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

const services = [
  {
    icon: 'ri-seedling-line',
    title: 'Holistic Healing Therapy',
    desc: 'An integrative approach addressing the whole person — body, mind, and energy field — to release deep-rooted tension and restore inner harmony through advanced hands-on techniques.',
    tag: 'Most Requested',
    img: 'https://public.readdy.ai/ai/img_res/6ae2b09b668b4201fd3c28bce3dfd370.jpg',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Energy Work & Bodywork',
    desc: 'Targeted energetic and physical bodywork that improves circulation, releases energetic blockages, and restores natural flow throughout the body\'s interconnected systems.',
    tag: '',
    img: 'https://readdy.ai/api/search-image?query=Serene%20energy%20healing%20session%20in%20a%20dark%20candle-lit%20luxury%20treatment%20room%2C%20healers%20hands%20hovering%20with%20intention%20above%20a%20peacefully%20resting%20client%2C%20soft%20golden%20warm%20light%20emanating%2C%20crystals%20arranged%20nearby%20on%20dark%20slate%2C%20spiritual%20premium%20wellness%20atmosphere%2C%20photorealistic%2C%20ultra-luxurious&width=700&height=480&seq=svc2002&orientation=landscape',
  },
  {
    icon: 'ri-mental-health-line',
    title: 'Trauma Release & Nervous System Reset',
    desc: 'Specialized somatic techniques designed to safely discharge stored trauma, calm an overactivated nervous system, and rebuild resilience from the deepest levels.',
    tag: 'Transformative',
    img: 'https://readdy.ai/api/search-image?query=A%20person%20in%20a%20serene%20meditative%20state%20lying%20on%20a%20luxury%20dark%20linen%20treatment%20table%20in%20a%20candlelit%20sanctuary%2C%20healing%20hands%20positioned%20gently%20on%20their%20neck%20and%20shoulders%2C%20deeply%20calming%20somatic%20nervous%20system%20release%20session%2C%20dark%20rich%20textures%2C%20warm%20amber%20glows%2C%20photorealistic%20ultra%20premium%20wellness&width=700&height=480&seq=svc2003&orientation=landscape',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Personalized Healing Sessions',
    desc: 'Every session is uniquely tailored to your needs, combining multiple healing modalities into one focused, high-impact experience crafted around your goals and history.',
    tag: '',
    img: 'https://readdy.ai/api/search-image?query=Intimate%20one-on-one%20consultation%20between%20a%20skilled%20holistic%20practitioner%20and%20client%20in%20a%20warm%20amber-lit%20luxury%20healing%20studio%2C%20personalized%20treatment%20planning%20discussion%2C%20dark%20warm%20interior%20with%20plants%20and%20candles%2C%20trust%20and%20care%20in%20the%20atmosphere%2C%20photorealistic%2C%20high-end%20wellness%20environment&width=700&height=480&seq=svc2004&orientation=landscape',
  },
];

interface ServicesSectionProps {
  onScrollTo: (id: string) => void;
}

export default function ServicesSection({ onScrollTo }: ServicesSectionProps) {
  const { ref, inView } = useInView();

  return (
    <section
      id="services"
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-[#080808] py-32 border-t border-[#d4af37]/8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="w-8 h-px bg-[#d4af37]/30" />
              <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                What We Offer
              </p>
            </div>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Therapeutic Services<br />
              <em className="text-[#d4af37] not-italic">Designed for Deep,</em><br />
              Lasting Results
            </h2>
          </div>
          <div className="flex-shrink-0">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border border-[#d4af37]/20 hover:border-[#d4af37] text-[#d4af37] px-7 py-3 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              All Services
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </Link>
          </div>
        </div>

        {/* Service Cards — staggered layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((svc, idx) => (
            <div
              key={idx}
              className={`group relative bg-[#0d0d0d] border border-[#161616] hover:border-[#d4af37]/20 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                idx % 2 !== 0 ? 'md:mt-10' : ''
              } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              {/* Image */}
              <div className="relative w-full h-56 overflow-hidden">
                <img
                  src={svc.img}
                  alt={svc.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />
                {svc.tag && (
                  <div className="absolute top-4 right-4 bg-[#d4af37] text-[#080808] text-[9px] uppercase tracking-[0.2em] px-3 py-1 rounded-full font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {svc.tag}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#d4af37]/8 group-hover:bg-[#d4af37]/15 transition-colors flex-shrink-0">
                    <i className={`${svc.icon} text-[#d4af37] text-lg`} />
                  </div>
                  <h3
                    className="text-white text-xl font-light mt-1 leading-snug"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {svc.title}
                  </h3>
                </div>
                <p className="text-[#5a5a5a] text-sm leading-relaxed mb-7" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {svc.desc}
                </p>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => onScrollTo('booking')}
                    className="inline-flex items-center gap-2 text-[#d4af37] text-xs uppercase tracking-[0.15em] cursor-pointer hover:gap-3 transition-all duration-300 whitespace-nowrap"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Book This Service
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm" />
                    </div>
                  </button>
                  <Link to="/services" className="text-[#2a2a2a] hover:text-[#5a5a5a] text-xs uppercase tracking-widest transition-colors cursor-pointer whitespace-nowrap" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
